<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.xsOnly" :value="value" content-class="sp-dialog" max-width="1080"
            @input="$emit('input', $event)">
    <div class="sp" role="dialog" aria-label="Suche">
      <div class="sp-input">
        <v-icon>mdi-magnify</v-icon>
        <input ref="input" v-model="term" aria-label="Suche" autocomplete="off" placeholder="Suchen: Name, Nummer, Telefon, E-Mail, Ort …"
               spellcheck="false" type="text" @input="onTermInput" @keydown="onKeydown">
        <v-progress-circular v-if="loading" color="info" indeterminate size="18" width="2"/>
        <v-btn v-tip="$vuetify.breakpoint.xsOnly ? 'Schließen' : 'Schließen (Esc)'" aria-label="Schließen" icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div v-if="hasTerm" class="sp-filters">
        <button v-for="pill in pills" :key="pill.key" :aria-pressed="String(isPillActive(pill.key))"
                :class="{ 'pill--active': isPillActive(pill.key), 'sp-pill--empty': !pill.count }" class="pill" type="button"
                @click="togglePill(pill.key)">
          <v-icon v-if="pill.icon" class="pill__icon" x-small>{{ pill.icon }}</v-icon>
          {{ pill.label }} <span class="sp-pill__count">{{ pill.count }}</span>
        </button>
        <span class="sp-sep"/>
        <segmented-control :items="statusItems" :value="status" label="Status" @input="setStatus"/>
      </div>
      <div :class="{ 'sp-body--single': mobile }" class="sp-body">
        <div ref="results" :aria-busy="String(showProgress)" class="sp-results" role="listbox">
          <div v-if="showProgress" aria-hidden="true" class="sp-progress"/>
          <template v-if="hasTerm">
            <search-results v-if="groups.length" :active-key="activeKey" :compact-key="compactKey" :groups="groups" :mobile="mobile"
                            :term="term" @expand="expand" @hover="onHover(keyOf($event))" @navigate="navigate"
                            @open="open($event.hit, $event.newTab)" @toggle-compact="toggleCompact"
                            @toggle-group="toggleGroup"/>
            <div v-else-if="showProgress" aria-hidden="true">
              <div v-for="n in 5" :key="`sk${n}`" class="sp-hit" style="cursor: default">
                <span class="sp-skel sp-hit__ini"/>
                <span class="sp-hit__main d-flex flex-column" style="gap: 6px">
                  <span :style="{ width: `${70 - n * 7}%` }" class="sp-skel"/>
                  <span class="sp-skel" style="width: 35%"/>
                </span>
              </div>
            </div>
            <div v-else-if="!loading && searched" class="sp-empty">
              <template v-if="failed">
                Die Suche ist gerade nicht erreichbar –
                <button class="sp-link" type="button" @click="search">erneut versuchen</button>
              </template>
              <template v-else-if="hiddenByFilter">
                Keine Treffer in diesem Bereich –
                <button class="sp-link" type="button" @click="selected = []">alle Bereiche anzeigen</button>
              </template>
              <template v-else-if="status === 'active'">
                Keine Treffer unter aktiven Profilen –
                <button class="sp-link" type="button" @click="setStatus('all')">auch inaktive anzeigen</button>
              </template>
              <template v-else>Keine Treffer</template>
            </div>
          </template>
          <search-empty-state v-else :active-key="activeKey" :sections="emptySections" @clear-searches="clearSearches"
                              @hover="onHover($event.key)" @run="runEntry($event.entry, $event.newTab)" @unpin="unpin"/>
        </div>
        <div v-if="!mobile" class="sp-qv">
          <search-quick-view v-if="quickViewHit" :key="keyOf(quickViewHit)" :hit="quickViewHit" @navigate="navigate"
                             @open="open($event, false)" @open-tab="open($event, true)"/>
          <div v-else-if="!hasTerm" class="acq-muted" style="line-height: 1.5">
            <div class="acq-section-title mb-2">Schnellzugriff</div>
            Ohne Suchbegriff zeigt die Suche, was du zuletzt geöffnet und gesucht hast, und deine angehefteten Profile.
            Anheften kannst du Profile über den Stern in der Schnellansicht. Gespeichert wird nur in diesem Browser.
          </div>
        </div>
      </div>
      <div class="sp-foot">
        <span>↑↓ auswählen</span><span>↵ öffnen</span><span>{{ newTabHint }} neuer Tab</span><span>⇥ Filter wechseln</span>
        <span>{{ openHint }} öffnen</span>
        <span v-if="recentSearchesText" style="margin-left: auto">Zuletzt gesucht: {{ recentSearchesText }}</span>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {RawLocation} from 'vue-router';
import {SearchStatus} from '@/classes/dto/finder/request/Search.request.dto';
import SegmentedControl, {SegmentItem} from '@/components/common/modern/segmentedControl.vue';
import SearchResults, {ResultGroup} from '@/components/app/search/searchResults.vue';
import SearchEmptyState from '@/components/app/search/searchEmptyState.vue';
import SearchQuickView from '@/components/app/search/searchQuickView.vue';
import {ENTITY_BY_KEY, PaletteEntity, PaletteHit, routeOfEntity, SEARCH_ENTITIES, splitPrefix} from '@/components/app/search/searchEntities';
import {
  runSearch,
  runSearchWithAcquisition,
  runTrainingSearchSafe,
  SearchGroupResult,
  SearchResults as SearchResultMap,
  searchTargets,
} from '@/components/app/search/searchApi';
import {rankHits} from '@/components/app/search/searchRanking';
import {buildEmptySections, EmptyEntry, EmptySection} from '@/components/app/search/searchEmptyEntries';
import {loadSearchStatus, saveSearchStatus, SearchMemoryStore} from '@/components/app/search/searchStore';
import {canReadArea} from '@/components/app/search/searchAccess';
import {canSearchTrainings} from '@/components/app/search/searchTrainings';
import {shortcutLabel} from '@/helpers/shortcut.helper';

const DEBOUNCE_MS = 200;
const DEFAULT_LIMIT = 8;
const EXPANDED_LIMIT = 50;
/** Mouse hover is ignored shortly after keyboard navigation (scrolling moves rows under the resting mouse) */
const HOVER_LOCK_MS = 350;
/** Fast searches show no loading indicator (avoids flashing) */
const PROGRESS_DELAY_MS = 150;

/** Key of the separate, collapsed group of acquisition-only schools */
const ACQUISITION_GROUP_KEY = 'acquisitionInstitutions';
/** Key of the merged list of all areas ordered by relevance */
const RANKED_GROUP_KEY = 'ranked';

type Pill = { key: PaletteEntity | 'all'; label: string; icon?: string; count: number };

/**
 * Command palette of the global search (Cmd+K / Ctrl+K): hits of all areas ordered by relevance with filters and quick view,
 * recent visits, pinned profiles and recent searches without term.
 */
export default Vue.extend({
  components: {SegmentedControl, SearchResults, SearchEmptyState, SearchQuickView},
  props: {
    value: {type: Boolean, required: true},
  },
  data: () => ({
    term: '',
    selected: [] as PaletteEntity[],
    status: 'active' as SearchStatus,
    /** Finder results */
    results: {} as SearchResultMap,
    /** Acquisition-only schools (only if few Standorte were found), shown as separate collapsed group */
    acquisitionResult: null as SearchGroupResult | null,
    acquisitionExpanded: false,
    /** Trainings (separate frontend query, merged into the groups) */
    trainingResult: null as SearchGroupResult | null,
    /** The user moved the selection since the last search started (late results must not reset it) */
    selectionTouched: false,
    loading: false,
    searched: false,
    failed: false,
    expanded: [] as PaletteEntity[],
    expanding: [] as PaletteEntity[],
    seq: 0,
    debounceTimer: undefined as number | undefined,
    activeKey: '',
    compactKey: '',
    keyboardAt: 0,
    keyListener: null as ((e: KeyboardEvent) => void) | null,
    /** Loading indicator of the results, shown only for searches slower than PROGRESS_DELAY_MS */
    showProgress: false,
    progressTimer: undefined as number | undefined,
  }),
  computed: {
    mobile(): boolean {
      return this.$vuetify.breakpoint.smAndDown;
    },
    newTabHint(): string {
      return shortcutLabel('Enter');
    },
    openHint(): string {
      return shortcutLabel('K');
    },
    hasTerm(): boolean {
      return splitPrefix(this.term).rest.trim().length > 0;
    },
    availableEntities(): PaletteEntity[] {
      const withTrainings = canSearchTrainings();
      return SEARCH_ENTITIES.map((el) => el.key).filter((key) => (key !== 'trainings' || withTrainings) && canReadArea(key));
    },
    allResults(): SearchResultMap {
      return this.trainingResult ? {...this.results, trainings: this.trainingResult} : this.results;
    },
    pills(): Pill[] {
      const counts = this.availableEntities.map((key) => this.allResults[key]?.total || 0);
      return [
        {key: 'all', label: 'Alle', count: counts.reduce((sum, el) => sum + el, 0)},
        ...this.availableEntities.map((key, i) => ({key, label: ENTITY_BY_KEY[key].label, icon: ENTITY_BY_KEY[key].icon, count: counts[i]})),
      ];
    },
    statusItems(): SegmentItem[] {
      return [{value: 'active', text: 'Aktiv'}, {value: 'inactive', text: 'Inaktiv'}, {value: 'all', text: 'Alle'}];
    },
    /**
     * One list of all areas ordered by relevance (best match first, each row shows its type), followed by the
     * collapsed acquisition-only schools, which are never mixed in.
     */
    groups(): ResultGroup[] {
      const areas = SEARCH_ENTITIES
          .filter((config) => !this.selected.length || this.selected.includes(config.key))
          .map((config) => ({config, result: this.allResults[config.key] || {total: 0, items: []}}))
          .filter((area) => area.result.items.length > 0);
      const groups: ResultGroup[] = [];
      if (areas.length) {
        const visible: SearchResultMap = {};
        areas.forEach((area) => {
          visible[area.config.key] = area.result;
        });
        const single = areas.length === 1 ? areas[0].config : null;
        groups.push({
          key: RANKED_GROUP_KEY,
          entity: areas[0].config.key,
          label: single ? single.label : 'Beste Treffer',
          icon: single ? single.icon : 'mdi-sort-descending',
          total: areas.reduce((sum, area) => sum + Math.max(area.result.total, area.result.items.length), 0),
          items: rankHits(visible, splitPrefix(this.term).rest, SEARCH_ENTITIES.map((config) => config.key)),
          canExpand: false,
          expanding: false,
          showType: !single,
          more: areas
              .filter((area) => area.result.total > area.result.items.length && !this.expanded.includes(area.config.key))
              .map((area) => ({
                entity: area.config.key,
                label: area.config.label,
                remaining: area.result.total - area.result.items.length,
                expanding: this.expanding.includes(area.config.key),
              })),
        });
      }
      const acquisition = this.acquisitionGroup;
      if (acquisition) {
        groups.push(acquisition);
      }
      return groups;
    },
    acquisitionGroup(): ResultGroup | null {
      const result = this.acquisitionResult;
      if (!result || !result.items.length || (this.selected.length && !this.selected.includes('institutions'))) {
        return null;
      }
      return {
        key: ACQUISITION_GROUP_KEY,
        entity: 'institutions',
        label: 'Weitere Schulen (Akquise)',
        icon: 'mdi-school-outline',
        total: Math.max(result.total, result.items.length),
        items: result.items,
        canExpand: false,
        expanding: false,
        collapsible: true,
        collapsed: !this.acquisitionExpanded,
        hint: 'Noch nicht akquirierte Schulen aus Akquiselisten – keine Standorte.',
        showType: true,
        typeLabel: 'Akquise-Standort',
      };
    },
    flatHits(): PaletteHit[] {
      return this.groups.reduce((acc, group) => acc.concat(group.collapsed ? [] : group.items), [] as PaletteHit[]);
    },
    hiddenByFilter(): boolean {
      return this.selected.length > 0 && Object.values(this.allResults).some((group) => !!group && group.items.length > 0);
    },
    emptySections(): EmptySection[] {
      return this.hasTerm || !this.value ? [] : buildEmptySections();
    },
    emptyEntries(): EmptyEntry[] {
      return this.emptySections.reduce((acc, section) => acc.concat(section.entries), [] as EmptyEntry[]);
    },
    selectableKeys(): string[] {
      return this.hasTerm ? this.flatHits.map((hit) => this.keyOf(hit)) : this.emptyEntries.map((entry) => entry.key);
    },
    quickViewHit(): PaletteHit | undefined {
      if (this.hasTerm) {
        return this.flatHits.find((hit) => this.keyOf(hit) === this.activeKey);
      }
      return this.emptyEntries.find((entry) => entry.key === this.activeKey)?.hit;
    },
    recentSearchesText(): string {
      return this.value ? SearchMemoryStore.searches.slice(0, 3).map((el) => `„${el}“`).join(', ') : '';
    },
  },
  methods: {
    keyOf(hit: PaletteHit): string {
      return `${hit.entity}:${hit.id}`;
    },
    close() {
      this.$emit('input', false);
    },
    focusInput() {
      setTimeout(() => {
        const input = this.$refs.input as HTMLInputElement | undefined;
        if (input) {
          input.focus();
          input.select();
        }
      }, 60);
    },
    onTermInput() {
      clearTimeout(this.debounceTimer);
      if (!this.hasTerm) {
        this.seq++;
        this.results = {};
        this.acquisitionResult = null;
        this.trainingResult = null;
        this.loading = false;
        this.searched = false;
        this.resetActive();
        return;
      }
      this.debounceTimer = setTimeout(() => this.search(), DEBOUNCE_MS) as unknown as number;
    },
    /**
     * Runs the finder and the trainings query in parallel; each result is shown as soon as it arrives.
     * Responses of outdated requests are dropped, a failed trainings request only leaves its group empty.
     */
    async search() {
      clearTimeout(this.debounceTimer);
      if (!this.hasTerm) {
        return;
      }
      const seq = ++this.seq;
      const term = this.term.trim();
      const targets = searchTargets(term);
      this.loading = true;
      this.expanded = [];
      this.expanding = [];
      this.acquisitionExpanded = false;
      this.compactKey = '';
      this.selectionTouched = false;
      if (!targets.trainings) {
        this.trainingResult = null;
      }
      const tasks: Array<Promise<void>> = [targets.finder ? this.searchFinder(seq, term) : this.skipFinder()];
      if (targets.trainings) {
        tasks.push(runTrainingSearchSafe(term, this.status, DEFAULT_LIMIT).then((result) => {
          if (seq === this.seq) {
            this.trainingResult = result;
            this.afterResults();
          }
        }));
      }
      await Promise.all(tasks);
      if (seq === this.seq) {
        this.loading = false;
      }
    },
    async searchFinder(seq: number, term: string) {
      try {
        const {results, acquisitionInstitutions} = await runSearchWithAcquisition({
          term, status: this.status, limit: DEFAULT_LIMIT, includeAcquisitionInstitutions: canReadArea('institutions'),
        });
        if (seq !== this.seq) {
          return;
        }
        this.results = results;
        this.acquisitionResult = acquisitionInstitutions;
        this.failed = false;
        this.searched = true;
        this.afterResults();
      } catch (e) {
        if (seq === this.seq) {
          this.results = {};
          this.acquisitionResult = null;
          this.searched = true;
          this.failed = true;
          this.$$showSnackbar('Beim Laden der Suchergebnisse ist ein Fehler aufgetreten', 'error', e);
        }
      }
    },
    /** "sc:" searches trainings only */
    async skipFinder() {
      this.results = {};
      this.acquisitionResult = null;
      this.failed = false;
      this.searched = true;
    },
    /** Selects the first hit unless the user already moved the selection */
    afterResults() {
      if (!this.selectionTouched || !this.selectableKeys.includes(this.activeKey)) {
        this.resetActive();
      }
    },
    /** "alle anzeigen": loads up to 50 hits of one area */
    async expand(entity: PaletteEntity) {
      const seq = this.seq;
      this.expanding = [...this.expanding, entity];
      try {
        if (entity === 'trainings') {
          const trainings = await runTrainingSearchSafe(this.term.trim(), this.status, EXPANDED_LIMIT);
          if (seq === this.seq && trainings) {
            this.trainingResult = trainings;
            this.expanded = [...this.expanded, entity];
          }
          return;
        }
        const results = await runSearch({term: this.term.trim(), status: this.status, entities: [entity], limit: EXPANDED_LIMIT});
        const group = results[entity];
        if (seq === this.seq && group) {
          this.results = {...this.results, [entity]: group};
          this.expanded = [...this.expanded, entity];
        }
      } catch (e) {
        this.$$showSnackbar('Weitere Treffer konnten nicht geladen werden', 'error', e);
      } finally {
        this.expanding = this.expanding.filter((el) => el !== entity);
      }
    },
    /** Opens / closes the collapsed group of acquisition-only schools */
    toggleGroup(key: string) {
      if (key === ACQUISITION_GROUP_KEY) {
        this.acquisitionExpanded = !this.acquisitionExpanded;
        if (!this.selectableKeys.includes(this.activeKey)) {
          this.resetActive();
        }
      }
    },
    setStatus(status: SearchStatus) {
      if (this.status !== status) {
        this.status = status;
        saveSearchStatus(status);
        this.search();
      }
    },
    isPillActive(key: PaletteEntity | 'all'): boolean {
      return key === 'all' ? !this.selected.length : this.selected.includes(key);
    },
    togglePill(key: PaletteEntity | 'all') {
      if (key === 'all') {
        this.selected = [];
      } else {
        this.selected = this.selected.includes(key) ? this.selected.filter((el) => el !== key) : [...this.selected, key];
      }
      this.resetActive();
    },
    /** Tab / Shift+Tab: single filter forward / backward */
    cycleFilter(direction: number) {
      const order: Array<PaletteEntity | 'all'> = ['all', ...this.availableEntities];
      const current = this.selected.length ? this.selected[0] : 'all';
      const next = order[(order.indexOf(current) + direction + order.length) % order.length];
      this.selected = next === 'all' ? [] : [next];
      this.resetActive();
    },
    resetActive() {
      this.activeKey = this.selectableKeys[0] || '';
      this.$nextTick(() => {
        const container = this.$refs.results as HTMLElement | undefined;
        if (container) {
          container.scrollTop = 0;
        }
      });
    },
    onHover(key: string) {
      if (Date.now() - this.keyboardAt > HOVER_LOCK_MS && this.activeKey !== key) {
        this.activeKey = key;
        this.selectionTouched = true;
      }
    },
    move(delta: number) {
      const keys = this.selectableKeys;
      if (!keys.length) {
        return;
      }
      const index = keys.indexOf(this.activeKey);
      const next = index < 0 ? 0 : Math.min(keys.length - 1, Math.max(0, index + delta));
      this.activeKey = keys[next];
      this.keyboardAt = Date.now();
      this.selectionTouched = true;
      this.$nextTick(() => {
        const container = this.$refs.results as HTMLElement | undefined;
        const row = container ? Array.from(container.querySelectorAll('[data-key]')).find((el) => el.getAttribute('data-key') === this.activeKey) : null;
        if (row) {
          row.scrollIntoView({block: 'nearest'});
        }
      });
    },
    onKeydown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.move(e.key === 'ArrowDown' ? 1 : -1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.openActive(e.metaKey || e.ctrlKey);
      } else if (e.key === 'Tab' && this.hasTerm) {
        e.preventDefault();
        this.cycleFilter(e.shiftKey ? -1 : 1);
      }
    },
    openActive(newTab: boolean) {
      if (this.hasTerm) {
        if (this.loading && !this.flatHits.length) {
          return;
        }
        const hit = this.flatHits.find((el) => this.keyOf(el) === this.activeKey);
        if (hit) {
          this.open(hit, newTab);
        }
        return;
      }
      const entry = this.emptyEntries.find((el) => el.key === this.activeKey);
      if (entry) {
        this.runEntry(entry, newTab);
      }
    },
    open(hit: PaletteHit, newTab: boolean) {
      const route = hit.openTo || routeOfEntity(hit.entity, hit.id);
      if (!route) {
        return;
      }
      if (this.hasTerm) {
        SearchMemoryStore.addSearch(this.term);
      }
      this.go(route, newTab);
    },
    navigate(to: RawLocation, newTab = false) {
      if (this.hasTerm) {
        SearchMemoryStore.addSearch(this.term);
      }
      this.go(to, newTab);
    },
    go(to: RawLocation, newTab: boolean) {
      if (newTab) {
        window.open(this.$router.resolve(to).href, '_blank', 'noopener');
        return;
      }
      this.close();
      this.$router.push(to).catch(() => undefined);
    },
    runEntry(entry: EmptyEntry, newTab: boolean) {
      if (entry.term) {
        this.term = entry.term;
        this.search();
        this.focusInput();
      } else if (entry.to) {
        this.go(entry.to, newTab);
      }
    },
    toggleCompact(hit: PaletteHit) {
      const key = this.keyOf(hit);
      this.compactKey = this.compactKey === key ? '' : key;
      this.activeKey = key;
    },
    clearSearches() {
      SearchMemoryStore.clearSearches();
      this.resetActive();
    },
    unpin(entry: EmptyEntry) {
      if (entry.hit) {
        SearchMemoryStore.togglePin({entity: entry.hit.entity, id: entry.hit.id, title: entry.hit.title});
      }
    },
    onGlobalKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        this.$emit('input', !this.value);
      }
    },
  },
  watch: {
    loading(isLoading: boolean) {
      clearTimeout(this.progressTimer);
      if (!isLoading) {
        this.showProgress = false;
        return;
      }
      this.progressTimer = setTimeout(() => {
        this.showProgress = this.loading;
      }, PROGRESS_DELAY_MS) as unknown as number;
    },
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          SearchMemoryStore.load();
          const stored = loadSearchStatus<SearchStatus>();
          if (stored && stored !== this.status) {
            this.status = stored;
            if (this.hasTerm) {
              this.search();
            }
          }
          this.focusInput();
          this.$nextTick(() => {
            if (!this.activeKey || !this.selectableKeys.includes(this.activeKey)) {
              this.resetActive();
            }
          });
        }
      },
    },
  },
  mounted() {
    this.keyListener = (e: KeyboardEvent) => this.onGlobalKeydown(e);
    window.addEventListener('keydown', this.keyListener);
  },
  beforeDestroy() {
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener);
    }
    clearTimeout(this.debounceTimer);
    clearTimeout(this.progressTimer);
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="./searchPalette.css"></style>
